import { useEffect, useRef, type RefObject } from 'react';
import { isNil } from 'lodash';

interface ApplyOnEnterOptions {
    onApply: () => void;
    canApply: () => boolean;
    isEnabled?: boolean;
}

interface ApplyOnEnterResult<T extends HTMLElement> {
    containerRef: RefObject<T>;
    handleInteraction: () => void;
}

/**
 * Calls `onApply` on Enter after the user worked with the filters inside `containerRef`.
 * Bind `handleInteraction` to the container's `onFocus` and `onMouseDown`.
 *
 * Gene UI dropdowns and date pickers blur themselves after a value is picked, so Enter is also handled when
 * nothing is focused. An open dropdown and inputs rendered in portals (e.g. dropdown or option list search)
 * keep their own Enter. `canApply` is checked on every Enter; it should read the current state
 * (e.g. `hasUnappliedChanges` of `useFilterFieldsState`) rather than a debounced flag.
 */
const useApplyOnEnter = <T extends HTMLElement = HTMLDivElement>({
    onApply,
    canApply,
    isEnabled = true,
}: ApplyOnEnterOptions): ApplyOnEnterResult<T> => {
    const containerRef = useRef<T>(null);
    // True while the last click or focus was in the container, including its portaled popovers
    const isInteractingRef = useRef(false);
    const optionsRef = useRef({ onApply, canApply });

    optionsRef.current = { onApply, canApply };

    const handleInteraction = () => {
        isInteractingRef.current = true;
    };

    useEffect(() => {
        if (!isEnabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Enter' || e.isComposing || !isInteractingRef.current || !optionsRef.current.canApply()) {
                return;
            }

            const { activeElement } = document;
            const isNothingFocused = isNil(activeElement) || activeElement === document.body;
            const isFilterInputFocused =
                activeElement instanceof HTMLInputElement &&
                !!containerRef.current?.contains(activeElement) &&
                isNil(activeElement.closest('.dropdown-holder .popover-opened'));

            if (!isNothingFocused && !isFilterInputFocused) return;

            e.preventDefault();
            e.stopPropagation();
            optionsRef.current.onApply();
        };

        // Capture phase runs before Gene UI's own keydown listeners and before React's handlers,
        // so a click or focus outside resets the flag first and the container handlers set it back.
        const resetInteraction = () => {
            isInteractingRef.current = false;
        };

        document.addEventListener('keydown', handleKeyDown, true);
        document.addEventListener('mousedown', resetInteraction, true);
        document.addEventListener('focusin', resetInteraction, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('mousedown', resetInteraction, true);
            document.removeEventListener('focusin', resetInteraction, true);
        };
    }, [isEnabled]);

    return { containerRef, handleInteraction };
};

export default useApplyOnEnter;
